import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { appRoutes } from './app/app.routes';
import { environment } from './environments/environment';
import { AppConfig } from './app/Shared/app-config.inject';
import { registerLicense } from '@syncfusion/ej2-base';
import { APP_INITIALIZER, importProvidersFrom } from '@angular/core';
import { ToastrModule } from 'ngx-toastr';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TokenInterceptor } from './app/Shared/token.interceptor';
import { Capacitor } from '@capacitor/core';
import { migrateTenantStorage, TenantService } from './app/services/tenant.service';

import { addIcons } from 'ionicons';
import { arrowBackOutline, notificationsOutline } from 'ionicons/icons';

addIcons({
  'arrow-back-outline': arrowBackOutline,
  'notifications-outline': notificationsOutline
});

const requestOrigin = window.location.origin;

async function bootstrapApp(): Promise<void> {
  let appConfig: any = {};

  if (Capacitor.isNativePlatform()) {
    try {
      const store = await migrateTenantStorage();
      const activeProfile = store.profiles.find(p => p.id === store.activeProfileId) ?? null;
      if (activeProfile) {
        appConfig.apiBaseUrl    = activeProfile.apiBaseUrl    ?? '';
        appConfig.syncfusionKey = activeProfile.syncfusionKey ?? environment.syncfusionKey;
        appConfig.production    = true;

        const liveKey = await fetchSyncfusionKeyFromServer(appConfig.apiBaseUrl);
        if (liveKey) {
          appConfig.syncfusionKey = liveKey;
        }
      }
    } catch (e) {
      console.warn('main.ts: could not read tenant profile from Preferences', e);
    }
  } else {
    appConfig = await loadWebConfig();
  }

  environment.apiBaseUrl    = appConfig.apiBaseUrl    ?? environment.apiBaseUrl;
  environment.syncfusionKey = appConfig.syncfusionKey ?? environment.syncfusionKey;
  environment.production    = appConfig.production    ?? environment.production;

  if (environment.syncfusionKey && !environment.syncfusionKey.startsWith('{')) {
    registerLicense(environment.syncfusionKey);
  }

  // FIX: on native, window.location.origin is http://localhost (the WebView),
  // NOT the server. Overriding here broke every API call after an app restart.
  // Only the web build should derive the API URL from its own origin.
  if (!Capacitor.isNativePlatform()) {
    environment.apiBaseUrl = requestOrigin + '/api';
  }
  console.log('baseURL =', environment.apiBaseUrl, '| platform =', Capacitor.getPlatform());

  await bootstrapApplication(AppComponent, {
    providers: [
      provideAnimations(),
      provideHttpClient(withInterceptorsFromDi()),
      {
        provide: HTTP_INTERCEPTORS,
        useClass: TokenInterceptor,
        multi: true
      },
      // FIX: hydrate TenantService's in-memory store BEFORE any guard/page runs,
      // so getConfig()/getVendorId()/getMarketId() are not null on cold start.
      {
        provide: APP_INITIALIZER,
        useFactory: (tenantService: TenantService) => () => tenantService.loadSavedConfig(),
        deps: [TenantService],
        multi: true
      },
      CurrencyPipe,
      DatePipe,
      provideRouter(appRoutes),
      {
        provide: AppConfig,
        useValue: { ...appConfig }
      },
      importProvidersFrom(
        ToastrModule.forRoot({
          timeOut: 3000,
          positionClass: 'toast-bottom-right',
          preventDuplicates: true
        })
      )
    ]
  }).catch(err => console.error(err));
}

function loadWebConfig(): Promise<any> {
  return new Promise((resolve) => {
    const req = new XMLHttpRequest();
    req.addEventListener('load', (res: any) => {
      try {
        resolve(JSON.parse(res.currentTarget.response));
      } catch {
        resolve({});
      }
    });
    req.addEventListener('error', () => resolve({}));
    req.open('GET', '/assets/config/config.json');
    req.send(null);
  });
}

function fetchSyncfusionKeyFromServer(apiBaseUrl: string): Promise<string | null> {
  return new Promise((resolve) => {
    const origin = apiBaseUrl.replace(/\/api\/?$/i, '');
    const req = new XMLHttpRequest();
    req.addEventListener('load', (res: any) => {
      try {
        const cfg = JSON.parse(res.currentTarget.response);
        resolve(cfg?.syncfusionKey ?? null);
      } catch {
        resolve(null);
      }
    });
    req.addEventListener('error', () => resolve(null));
    req.open('GET', `${origin}/assets/config/config.json`);
    req.send(null);
  });
}

bootstrapApp();